// Customer (fleet) portal: balances, invoice history per truck, units in the shop. Sign in by email link.
import { SharedPhotos } from '../photos';
import { useEffect, useMemo, useState } from 'react';
import { api, useApi, useLocation, match, navigate, dollars, fmtDate, ago, today, invoiceStatus, isOverdueDate, setGrace, downloadCsv, DEMO, photoSrc, cleanPasted, type Row } from '../lib';
import { Badge, Button, Icon, Loading, ErrorBox, Empty, Stat, Table, Panel, Tabs, Link, Modal, Form, Field, Select, TextArea, SearchBox, useToast } from '../ui';
import { TRUCK_TYPES, TRAILER_TYPES } from '../../shared/calc';
import { InvoiceSheet, EstimateSheet, ReceiptSheet, logoUrl } from '../invoice-sheet';

const FRIENDLY: Record<string, string> = {
  New: 'Checked in', Diagnosing: 'Diagnosing', 'Waiting Parts': 'Waiting on parts', 'Waiting Approval': 'Waiting for your approval', 'In Progress': 'Being repaired', Ready: 'Ready for pickup', Completed: 'Done — invoice coming',
};
const STEPS = ['New', 'Diagnosing', 'In Progress', 'Ready'];

const ONE_DOC = { on: false };
const setOneDoc = (v: boolean) => { ONE_DOC.on = v; };

export function PortalApp() {
  const { path } = useLocation();
  const [session, setSession] = useState(0);
  const [requesting, setRequesting] = useState(false);
  const summary = useApi<Row>('/api/portal/summary', [session]);
  const shop = useApi<Row>('/api/portal/shop');
  if (shop.data) setGrace(shop.data.overdue_grace);
  if (summary.error && /sign in/i.test(summary.error)) return <PortalLogin shop={shop.data} />;
  if (summary.error) return <div className="portal"><ErrorBox error={summary.error} onRetry={summary.reload} /></div>;
  if (!summary.data) return <Loading />;
  const d = summary.data;
  const m = match('/portal/invoices/:id', path);
  const me = match('/portal/estimates/:id', path);
  const mr = match('/portal/receipts/:no', path);
  // A link for one document: only that document — no account, no other trucks, no way "back" to the list.
  const scoped = String(d.scoped || '');
  const scopedPath = scoped.startsWith('invoice:') ? `/portal/invoices/${scoped.slice(8)}` : scoped.startsWith('estimate:') ? `/portal/estimates/${scoped.slice(9)}` : scoped.startsWith('receipt:') ? `/portal/receipts/${encodeURIComponent(scoped.slice(8))}` : '';
  if (scoped && path !== scopedPath && decodeURIComponent(path) !== decodeURIComponent(scopedPath)) { setTimeout(() => navigate(scopedPath), 0); return <Loading />; }
  setOneDoc(!!scoped);
  return (
    <div className="portal">
      <header className="portal-top">
        <Link to={scoped ? scopedPath : '/portal'} className="portal-logo"><img src={logoUrl(d.shop.logo_version)} alt={d.shop.shop_name} /></Link>
        <div className="portal-who">
          <b>{d.customer.name}</b>
          <small>{d.email}</small>
        </div>
        <div className="portal-actions">
          {!m && !me && !mr && !scoped && <Button kind="danger" icon="wrench" onClick={() => setRequesting(true)}><span>Request service</span></Button>}
          {d.shop.shop_phone && <a className="btn secondary" href={`tel:${d.shop.shop_phone}`}><Icon name="phone" size={16} /><span className="hide-sm">{d.shop.shop_phone}</span></a>}
          <button className="icon-btn" title="Sign out" aria-label="Sign out" onClick={async () => { await api('/api/portal/logout', { body: {} }); navigate('/portal'); setSession(n => n + 1); }}><Icon name="logout" /></button>
        </div>
      </header>
      {scoped && <div className="note-bar one-doc no-print"><Icon name="lock" size={16} /><span className="grow">This link shows <b>this document only</b>. To see your account, sign in with your email.</span>
        <Button onClick={async () => { await api('/api/portal/logout', { body: {} }); navigate('/portal'); setSession(n => n + 1); }}>Sign in</Button></div>}
      <main className="portal-main">{mr ? <PortalReceipt no={decodeURIComponent(mr.no)} /> : me ? <PortalEstimate id={me.id} /> : m ? <PortalInvoice id={m.id} /> : <PortalHome d={d} reload={summary.reload} onRequest={() => setRequesting(true)} />}</main>
      {requesting && <RequestService d={d} onClose={() => setRequesting(false)} onDone={() => { setRequesting(false); summary.reload(); }} />}
      <footer className="portal-foot">{d.shop.shop_name} · {d.shop.shop_address} · {d.shop.shop_phone}</footer>
    </div>
  );
}

function PortalLogin({ shop }: { shop: Row | null }) {
  const { query } = useLocation();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'manual'>('idle');
  const [err, setErr] = useState('');
  return (
    <div className="auth portal-auth">
      <div className="auth-card">
        <img className="auth-logo" src={logoUrl(shop?.logo_version)} alt={shop?.shop_name || 'Logo'} />
        <h1>Customer account</h1>
        {query.get('expired') && state === 'idle' && <div className="note-bar warn"><Icon name="alert" size={16} />That link has expired. Enter your email for a new one.</div>}
        {state === 'sent' ? (
          <>
            <p>If <b>{email}</b> is on file with us, a sign-in link is on its way. It works for 30 minutes.</p>
            <p className="muted small">Nothing arrived? Check spam, or call us{shop?.shop_phone ? ` at ${shop.shop_phone}` : ''}.</p>
            <Button onClick={() => setState('idle')}>Use a different email</Button>
          </>
        ) : state === 'manual' ? (
          <p>Please call us{shop?.shop_phone ? <> at <a href={`tel:${shop.shop_phone}`}>{shop.shop_phone}</a></> : ''} and we'll text you a link to your account.</p>
        ) : (
          <form className="form" onSubmit={async e => {
            e.preventDefault(); setState('busy'); setErr('');
            try {
              const r = await api<{ emailDisabled?: boolean }>('/api/portal/request-link', { body: { email } });
              setState(r.emailDisabled ? 'manual' : 'sent');
            } catch (x) { setErr((x as Error).message); setState('idle'); }
          }}>
            <p>See your invoices, balances and trucks in the shop. Enter the email we have for your company and we'll send you a sign-in link — no password needed.</p>
            <label className="field full"><span>Email</span><input type="email" required value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" /></label>
            {err && <div className="error-box"><Icon name="alert" />{err}</div>}
            <Button kind="primary" type="submit" busy={state === 'busy'} className="full big">Email me a link</Button>
          </form>
        )}
      </div>
    </div>
  );
}

function PortalHome({ d, reload, onRequest }: { d: Row; reload: () => void; onRequest: () => void }) {
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState('unpaid');
  const [unit, setUnit] = useState('');
  const invoices = d.invoices as Row[];
  const year = today().slice(0, 4);
  const open = invoices.filter(i => i.paid < i.total);
  const balance = open.reduce((a, i) => a + i.total - i.paid, 0);
  const overdue = open.filter(i => isOverdueDate(i.due_on));
  const overdueAmt = overdue.reduce((a, i) => a + i.total - i.paid, 0);
  const paidYear = invoices.filter(i => i.issued_on.startsWith(year)).reduce((a, i) => a + i.paid, 0);
  const unitKey = (i: Row) => i.equipment_id || 'u:' + i.unit;
  const units = useMemo(() => {
    const map = new Map<string, Row>();
    for (const u of d.units as Row[]) map.set(u.id, { key: u.id, unit: u.unit, type: u.type, name: [u.year, u.make, u.model].filter(Boolean).join(' '), balance: 0, count: 0, last: '' });
    for (const i of invoices) {
      const k = unitKey(i);
      if (!map.has(k)) map.set(k, { key: k, unit: i.unit, type: i.unit_type || 'Unit', name: '', balance: 0, count: 0, last: '' });
      const u = map.get(k)!;
      u.balance += i.total - i.paid; u.count += 1; if (i.issued_on > u.last) u.last = i.issued_on;
    }
    return [...map.values()].sort((a, b) => b.balance - a.balance || String(a.unit).localeCompare(String(b.unit)));
  }, [d]);
  const list = invoices.filter(i => (tab === 'unpaid' ? i.paid < i.total : tab === 'paid' ? i.paid >= i.total : true) && (!unit || unitKey(i) === unit));
  const selectedUnit = units.find(u => u.key === unit);
  const [q, setQ] = useState('');
  const found = useMemo(() => portalSearch(d, q), [d, q]);

  return (
    <>
      <div className="page-head"><div><h1>Your account</h1><p>Invoices and repairs for every truck and trailer we service for {d.customer.name}.</p></div></div>
      <div className="portal-search">
        <SearchBox value={q} onChange={setQ} placeholder="Search: invoice #, PO #, unit #, last 6 of VIN" />
        {q.trim().length >= 2 && <div className="ps-results">
          {found.length ? <ul className="list">{found.map(r => (
            <li key={r.kind + r.id} className="clickable" onClick={() => navigate(r.to)}>
              <span><Badge tone={r.kind === 'Invoice' ? 'blue' : r.kind === 'Estimate' ? 'amber' : 'green'}>{r.kind}</Badge> <b>{r.number}</b>
                <small className="muted block">{[r.unit ? `${r.unit_type || 'Unit'} #${r.unit}` : '', r.po ? `PO ${r.po}` : '', r.vin ? `VIN …${String(r.vin).slice(-6)}` : '', r.date ? fmtDate(r.date) : ''].filter(Boolean).join(' · ')}</small></span>
              <span className="right">{r.balance !== undefined ? (r.balance > 0 ? <b className="danger">{dollars(r.balance)}</b> : <span className="muted">Paid</span>) : r.status ? <span className="muted">{r.status}</span> : null}</span>
            </li>))}</ul> : <p className="muted pad">Nothing found for “{q.trim()}”. Try the invoice number, PO number, unit number or the last 6 of the VIN.</p>}
        </div>}
      </div>
      <div className="stats">
        <Stat label="Balance due" value={dollars(balance)} hint={`${open.length} open invoice${open.length === 1 ? '' : 's'}`} onClick={() => { setTab('unpaid'); setUnit(''); }} />
        <Stat label="Past due" value={dollars(overdueAmt)} tone={overdueAmt ? 'warn' : ''} hint={overdue.length ? `${overdue.length} invoice${overdue.length === 1 ? '' : 's'}` : 'Nothing past due'} />
        <Stat label={`Paid in ${year}`} value={dollars(paidYear)} hint="Thank you!" onClick={() => setTab('paid')} />
        <Stat label="In the shop now" value={(d.openJobs as Row[]).length} hint="Trucks & trailers" />
      </div>

      {(d.openJobs as Row[]).length > 0 && (
        <Panel title="In the shop now">
          <div className="shop-now">
            {(d.openJobs as Row[]).map(j => {
              const step = Math.max(0, STEPS.indexOf(j.status === 'Waiting Parts' || j.status === 'Waiting Approval' ? 'Diagnosing' : j.status === 'Completed' ? 'Ready' : j.status));
              return (
                <div key={j.id} className={'shop-job' + (j.shared ? ' open' : '')} role={j.shared ? 'button' : undefined} tabIndex={j.shared ? 0 : undefined}
                  onClick={() => j.shared && navigate('/portal/estimates/' + j.id)} onKeyDown={e => { if (j.shared && e.key === 'Enter') navigate('/portal/estimates/' + j.id); }}>
                  <div className="job-row"><b className="unit">{j.unit_type || 'Unit'} #{j.unit || '—'}</b><Badge tone={j.status === 'Ready' || j.status === 'Completed' ? 'green' : j.status === 'Waiting Parts' ? 'amber' : j.status === 'Waiting Approval' ? 'red' : 'blue'}>{FRIENDLY[j.status] || j.status}</Badge></div>
                  <p>{j.complaint}</p>
                  <div className="progress" aria-hidden="true">{STEPS.map((s, i) => <i key={s} className={i <= step ? 'on' : ''} />)}</div>
                  <small className="muted">{j.number} · checked in {fmtDate(j.created_at)} · updated {ago(j.updated_at)}</small>
                  {j.shared ? <span className={'shop-job-go' + (j.status === 'Waiting Approval' ? ' ask' : '')}>{j.status === 'Waiting Approval' ? 'Needs your approval — open →' : 'See the work & prices →'}</span>
                    : <small className="muted">The shop sends you the estimate when it's ready.</small>}
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      <div className="grid-2 wide-left">
        <Panel title={selectedUnit ? `Invoices — ${selectedUnit.type} #${selectedUnit.unit}` : 'Invoices'} action={<div className="actions">
          <select value={unit} onChange={e => setUnit(e.target.value)} aria-label="Filter by unit"><option value="">All units</option>{units.map(u => <option key={u.key} value={u.key}>#{u.unit}</option>)}</select>
          <Button icon="download" onClick={() => downloadCsv('invoices.csv', ['Invoice', 'Date', 'Unit', 'Total', 'Paid', 'Balance', 'Due'], list.map(i => [i.number, i.issued_on, i.unit, i.total / 100, i.paid / 100, (i.total - i.paid) / 100, i.due_on]))}>CSV</Button>
        </div>}>
          <Tabs value={tab} onChange={setTab} tabs={[{ id: 'unpaid', label: `Unpaid (${open.length})` }, { id: 'paid', label: 'Paid history' }, { id: 'all', label: 'All' }, ...((d.receipts || []).length ? [{ id: 'receipts', label: `Payments & receipts (${d.receipts.length})` }] : [])]} />
          {tab === 'receipts' ? <Table heads={['Receipt', 'Date', 'Paid on', 'Method', 'Amount']} rows={(d.receipts as Row[]).map(r => [<b>{r.receipt_no}</b>, fmtDate(r.paid_on), r.invoices, `${r.method}${r.reference ? ' · ' + r.reference : ''}`, <b>{dollars(r.total)}</b>])}
            onRow={i => navigate('/portal/receipts/' + encodeURIComponent((d.receipts as Row[])[i].receipt_no))} /> :
          <Table heads={['Invoice', 'Date', 'Unit', 'Total', 'Balance', 'Status']} empty={tab === 'unpaid' ? 'No unpaid invoices. Thank you!' : 'No invoices yet.'}
            rows={list.map(i => {
              const st = invoiceStatus({ ...i, void: 0 } as never);
              return [<b>{i.number}</b>, fmtDate(i.issued_on), `#${i.unit || '—'}`, dollars(i.total), <b className={st === 'Overdue' ? 'danger' : ''}>{dollars(i.total - i.paid)}</b>,
                <><Badge>{st === 'Overdue' ? 'Overdue' : st}</Badge>{st !== 'Paid' && <small className="muted">due {fmtDate(i.due_on)}</small>}</>];
            })}
            onRow={i => navigate('/portal/invoices/' + list[i].id)} />}
        </Panel>
        <div className="stack">
          <Panel title="Your units" action={<Button icon="plus" onClick={() => setAdding(true)}>Add unit</Button>}>
            {units.length ? (
              <ul className="list">
                {units.map(u => (
                  <li key={u.key} className={'clickable' + (unit === u.key ? ' selected' : '')} onClick={() => { setUnit(unit === u.key ? '' : u.key); setTab('all'); }}>
                    <span><b>{u.type} #{u.unit}</b><small className="muted block">{u.name || '—'}{u.last ? ` · last invoice ${fmtDate(u.last)}` : ''}</small></span>
                    <span className="right">{u.balance > 0 ? <b className="danger">{dollars(u.balance)}</b> : <span className="muted">Paid up</span>}<small className="muted block">{u.count} invoice{u.count === 1 ? '' : 's'}</small></span>
                  </li>
                ))}
              </ul>
            ) : <Empty>No units yet.</Empty>}
          </Panel>
          <Panel title="Need something fixed?"><div className="pad"><p className="muted">Book a truck in or ask for a road call — we'll call you back.</p>
            <Button kind="danger" icon="wrench" className="full" onClick={onRequest}>Request service</Button></div></Panel>
          {d.shop.payment_instructions && <Panel title="How to pay"><p className="pad">{d.shop.payment_instructions}</p></Panel>}
        </div>
      </div>
      <FleetPanel d={d} onAdd={() => setAdding(true)} />
      {adding && <AddUnit onClose={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />}
    </>
  );
}

/** Portal search: one box finds invoices, estimates and trucks in the shop by number, PO, unit or VIN (any part, e.g. last 6). */
const norm = (x: unknown) => cleanPasted(String(x ?? '')).toUpperCase().replace(/[^A-Z0-9]/g, '');
function portalSearch(d: Row, q: string) {
  const k = norm(q);
  if (k.length < 2) return [];
  const hit = (...xs: unknown[]) => xs.some(x => { const n = norm(x); return n && (n.includes(k) || (/^\d+$/.test(k) && n.replace(/^[A-Z]+/, '') === k)); });
  const out: Row[] = [];
  for (const i of d.invoices as Row[]) if (hit(i.number, i.wo_number, i.po_number, i.unit, i.vin)) out.push({ kind: 'Invoice', id: i.id, number: i.number, unit: i.unit, unit_type: i.unit_type, po: i.po_number, vin: i.vin, date: i.issued_on, balance: i.total - i.paid, to: '/portal/invoices/' + i.id });
  for (const e of (d.estimates || []) as Row[]) if (hit(e.est_number, e.number, e.po_number, e.unit, e.vin)) out.push({ kind: 'Estimate', id: e.id, number: e.est_number, unit: e.unit, unit_type: e.unit_type, po: e.po_number, vin: e.vin, date: e.created_at, status: ({ sent: 'Waiting for your answer', approved: 'Approved', declined: 'Declined', partial: 'Partly approved' } as Row)[e.est_status] || e.est_status, to: '/portal/estimates/' + e.id });
  for (const j of d.openJobs as Row[]) if (hit(j.number, j.po_number, j.unit, j.vin)) out.push({ kind: 'In the shop', id: j.id, number: j.number, unit: j.unit, unit_type: j.unit_type, po: j.po_number, vin: j.vin, date: j.created_at, status: FRIENDLY[j.status] || j.status, to: j.shared ? '/portal/estimates/' + j.id : '/portal' });
  return out.slice(0, 50);
}

/** Every truck and trailer on the account: what it is, VIN, in the shop now or not, last service, what's owed. CSV = fleet report. */
function FleetPanel({ d, onAdd }: { d: Row; onAdd: () => void }) {
  const units = d.units as Row[], open = d.openJobs as Row[], invoices = d.invoices as Row[];
  const rows: Row[] = units.map(u => {
    const inShop = open.find(j => j.equipment_id === u.id);
    const owed = invoices.filter(i => i.equipment_id === u.id).reduce((a, i) => a + i.total - i.paid, 0);
    return { ...u, inShop, owed, name: [u.year, u.make, u.model].filter(Boolean).join(' ') };
  });
  return (
    <Panel title={`Your fleet (${units.length})`} action={<div className="actions">
      <Button icon="download" onClick={() => downloadCsv('fleet-report.csv', ['Unit', 'Type', 'Year', 'Make', 'Model', 'VIN', 'Plate', 'Status', 'Last service', 'Balance'],
        rows.map(u => [u.unit, u.type, u.year, u.make, u.model, u.vin, u.plate, u.inShop ? (FRIENDLY[u.inShop.status] || u.inShop.status) : 'On the road', u.last_service || '', u.owed / 100]))}>Fleet report</Button>
      <Button icon="plus" onClick={onAdd}>Add unit</Button></div>}>
      <Table heads={['Unit', 'Type', 'Year / make / model', 'VIN', 'Status', 'Last service', 'Balance']} empty="No units yet — add your trucks and trailers."
        rows={rows.map(u => [<b>#{u.unit}</b>, u.type, u.name || '—', <small className="mono">{u.vin || '—'}</small>,
          u.inShop ? <Badge tone="blue">{FRIENDLY[u.inShop.status] || u.inShop.status}</Badge> : <span className="muted">—</span>,
          u.last_service ? fmtDate(u.last_service) : <span className="muted">—</span>, u.owed > 0 ? <b className="danger">{dollars(u.owed)}</b> : <span className="muted">—</span>])} />
    </Panel>
  );
}

function AddUnit({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  return (
    <Modal title="Add a unit" onClose={onClose}>
      <Form submitLabel="Add unit" onCancel={onClose} onSubmit={async v => {
        await api('/api/portal/units', { body: v }); toast(`Unit #${v.unit} added`); onDone();
      }}>
        <Field label="Unit #" name="unit" required autoFocus placeholder="e.g. 120" />
        <Select label="Type" name="type" defaultValue="Sleeper truck">{[...TRUCK_TYPES, ...TRAILER_TYPES].map(t => <option key={t}>{t}</option>)}</Select>
        <Field label="VIN" name="vin" maxLength={17} full placeholder="17 characters" style={{ textTransform: 'uppercase' }} />
        <Field label="Year" name="year" inputMode="numeric" maxLength={4} />
        <Field label="Make" name="make" placeholder="Freightliner, Volvo…" />
        <Field label="Model" name="model" placeholder="Cascadia, VNL…" />
        <Field label="Plate" name="plate" />
      </Form>
    </Modal>
  );
}

/** Book a truck in, or ask for a road call. Lands at the shop as a new work order. */
function RequestService({ d, onClose, onDone }: { d: Row; onClose: () => void; onDone: () => void }) {
  const [where, setWhere] = useState<'shop' | 'road'>('shop');
  const [unit, setUnit] = useState<string>((d.units as Row[])[0]?.id || '__other');
  const [sent, setSent] = useState<string>('');
  if (sent) return (
    <Modal title="Request sent" onClose={onDone}>
      <div className="pad center"><Icon name="check" size={36} /><h2>Thank you — we got it</h2>
        <p>Your request is <b>{sent}</b>. {d.shop.shop_name} will call you to confirm{where === 'road' ? ' and send a truck' : ''}.</p>
        {d.shop.shop_phone && <p className="muted">Urgent? Call <a href={`tel:${d.shop.shop_phone}`}>{d.shop.shop_phone}</a></p>}
        <Button kind="primary" onClick={onDone}>Done</Button></div>
    </Modal>
  );
  return (
    <Modal title="Request service" onClose={onClose}>
      <Form submitLabel={where === 'road' ? 'Send road call request' : 'Send request'} onCancel={onClose} onSubmit={async v => {
        const r = await api<{ number: string }>('/api/portal/service-request', { body: { ...v, where, equipment_id: unit === '__other' ? null : unit } });
        setSent(r.number);
      }}>
        <div className="seg-choice full" role="radiogroup" aria-label="Where">
          <button type="button" role="radio" aria-checked={where === 'shop'} className={where === 'shop' ? 'on' : ''} onClick={() => setWhere('shop')}>Bring it to the shop</button>
          <button type="button" role="radio" aria-checked={where === 'road'} className={where === 'road' ? 'on' : ''} onClick={() => setWhere('road')}>Road call — truck is broken down</button>
        </div>
        <label className="field"><span>Unit *</span>
          <select value={unit} onChange={e => setUnit(e.target.value)} aria-label="Unit">
            {(d.units as Row[]).map(u => <option key={u.id} value={u.id}>#{u.unit} · {u.type}{u.make ? ` · ${u.make}` : ''}</option>)}
            <option value="__other">Another unit (type it)</option>
          </select></label>
        {unit === '__other' ? <Field label="Unit #" name="unit_text" required /> : <span />}
        <TextArea label="What does it need?" name="problem" required rows={3} placeholder="e.g. PM service, air leak at rear, ABS light on…" />
        {where === 'road' && <Field label="Where is the truck?" name="location" required full placeholder="Highway / exit / address, city" />}
        <Field label={where === 'road' ? 'When' : 'Preferred date'} name="when" type="date" min={today()} defaultValue={where === 'road' ? today() : ''} />
        <Field label="Your name & phone" name="contact" defaultValue={[d.customer.contact, d.customer.phone].filter(Boolean).join(' · ')} />
      </Form>
    </Modal>
  );
}

function PortalReceipt({ no }: { no: string }) {
  const { data, error, reload } = useApi<Row>(`/api/portal/receipts/${encodeURIComponent(no)}`);
  const [withInv, setWithInv] = useState(true);
  const [invs, setInvs] = useState<Row[] | null>(null);
  // The invoices this payment covered come right under the receipt — on screen and in the PDF.
  useEffect(() => {
    if (!data || !withInv) return;
    Promise.all(((data.lines || []) as Row[]).map(l => api<Row>(`/api/portal/invoices/${l.invoice_id}`).catch(() => null))).then(x => setInvs(x.filter(Boolean) as Row[]));
  }, [data, withInv]);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const src = (pid: string) => (DEMO ? photoSrc(pid) : `/api/portal/photos/${pid}`);
  return (
    <>
      <div className="page-head no-print">
        <div>{!ONE_DOC.on && <Link to="/portal" className="back"><Icon name="back" size={16} />Your account</Link>}<h1>Receipt {no}</h1></div>
        <div className="actions rc-actions"><label className="check"><input type="checkbox" checked={withInv} onChange={e => setWithInv(e.target.checked)} /> With the invoices</label>
          <Button kind="primary" icon="printer" disabled={withInv && invs === null} onClick={() => window.print()}>Print / Save PDF</Button></div>
      </div>
      <ReceiptSheet d={data} shop={data.shop as Row} />
      {withInv && (invs === null ? <Loading /> : <div className="rc-invoices">{invs.map(x => <InvoiceSheet key={(x.invoice as Row).id} inv={x.invoice as Row} customer={x.customer as Row} workOrder={x.workOrder as Row} shop={x.shop as Row} photo={src} />)}</div>)}
    </>
  );
}

function PortalInvoice({ id }: { id: string }) {
  const { data, error, reload } = useApi<Row>(`/api/portal/invoices/${id}`);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const inv = data.invoice as Row;
  const st = invoiceStatus({ ...inv, void: 0 } as never);
  return (
    <>
      <div className="page-head no-print">
        <div>{!ONE_DOC.on && <Link to="/portal" className="back"><Icon name="back" size={16} />Your account</Link>}<h1>{inv.number} <Badge>{st}</Badge></h1></div>
        <div className="actions"><Button kind="primary" icon="printer" onClick={() => window.print()}>Print / Save PDF</Button></div>
      </div>
      <InvoiceSheet inv={inv} customer={data.customer} workOrder={data.workOrder} shop={data.shop} photo={pid => (DEMO ? photoSrc(pid) : `/api/portal/photos/${pid}`)} />
      <SharedPhotos photos={(data.photos || []) as Row[]} src={pid => (DEMO ? photoSrc(pid) : `/api/portal/photos/${pid}`)} />
      <AckDeclined inv={inv} onDone={reload} />
      {(data.payments as Row[]).length > 0 && (
        <Panel title="Payments received" className="no-print">
          <Table heads={['Date', 'Method', 'Reference', 'Amount', 'Receipt']} rows={(data.payments as Row[]).map(p => [fmtDate(p.paid_on), p.method, p.reference || '—', dollars(p.amount), p.receipt_no ? <Link to={'/portal/receipts/' + encodeURIComponent(p.receipt_no)}>{p.receipt_no}</Link> : '—'])} />
        </Panel>
      )}
    </>
  );
}

/** "I understand and decline": the customer signs for the advised work they turned down. */
function AckDeclined({ inv, onDone }: { inv: Row; onDone: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const todo = ((inv.advised || []) as Row[]).filter(r => r.status === 'declined' && !r.ack_name);
  if (!todo.length) return null;
  return (
    <Panel title="Work we advised that you declined" className="no-print">
      <div className="pad ack-box">
        <p>Our technician advised the repairs listed under <b>Advised — customer declined</b> on this invoice. Please confirm you were told and chose not to do them now.</p>
        <label className="field"><span>Your name (first and last)</span><input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. John Smith" autoComplete="name" /></label>
        {err && <div className="error-box"><Icon name="alert" />{err}</div>}
        <Button kind="primary" busy={busy} disabled={name.trim().split(/\s+/).length < 2} onClick={async () => {
          setBusy(true); setErr('');
          try { await api(`/api/portal/invoices/${inv.id}/acknowledge`, { body: { name } }); onDone(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
        }}>I understand and decline</Button>
        <small className="muted">Signed electronically — the date and time are added automatically.</small>
      </div>
    </Panel>
  );
}

/** An estimate (or more work found on a job) the shop sent: Approve / Decline on each job's own line, sign with your name, send.
 *  Answers are final once sent — earlier answers show on their jobs; changes go by phone. */
function PortalEstimate({ id }: { id: string }) {
  const { data, error, reload } = useApi<Row>(`/api/portal/estimates/${id}`);
  const [note, setNote] = useState('');
  const [name, setName] = useState('');
  const [pick, setPick] = useState<Record<string, 'approve' | 'decline'>>({});
  const [missing, setMissing] = useState<Set<string>>(new Set());
  const [nameBad, setNameBad] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const w = data.workOrder as Row;
  const shop = (data.shop || {}) as Row;
  const jobs = ((data.jobs as Row[]) || []).filter(j => j.id);
  const pending = jobs.filter(j => !j.decision);
  const done = jobs.filter(j => j.decision);
  const closed = !!w.invoice_id;
  const answeredWhole = !jobs.length && (w.doc !== 'estimate' || ['approved', 'declined', 'partial'].includes(w.est_status));
  const open = !closed && (jobs.length ? pending.length > 0 : !answeredWhole);
  const more = done.length > 0;
  const nameOk = name.trim().split(/\s+/).filter(Boolean).length >= 2 && name.trim().length >= 4;
  const send = async (body: Row) => {
    setBusy(true); setErr('');
    try { await api(`/api/portal/estimates/${id}/decision`, { body: { ...body, note, signed_name: name.trim() } }); setPick({}); setNote(''); reload(); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const trySend = () => {
    const left = pending.filter(j => !pick[j.id]);
    if (left.length) {
      // Jobs without an answer turn red and the page jumps to the first one.
      setMissing(new Set(left.map(j => String(j.id))));
      document.getElementById('job-' + left[0].id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setErr(`Please approve or decline ${left.length === 1 ? 'the job marked in red' : `the ${left.length} jobs marked in red`}.`);
      return;
    }
    if (!nameOk) { setNameBad(true); setErr('Please type your first and last name to sign.'); return; }
    void send({ jobs: pick });
  };
  const setOne = (jid: string, v: 'approve' | 'decline') => {
    setPick(p => ({ ...p, [jid]: v }));
    if (missing.has(jid)) { const m = new Set(missing); m.delete(jid); setMissing(m); if (!m.size) setErr(''); }
  };
  const okSum = pending.filter(j => pick[j.id] === 'approve').reduce((a, j) => a + Number(j.amount || 0), 0);
  const left = pending.filter(j => !pick[j.id]).length;
  const yes = done.filter(j => j.decision === 'approved' || j.decision === 'preapproved').length;
  const result = !jobs.length ? (w.doc !== 'estimate' ? 'Approved — we are working on it.' : w.est_status === 'declined' ? 'You declined this estimate.' : 'You approved this estimate. Thank you — we will get started.')
    : yes === done.length ? `You approved ${done.length === 1 ? 'this job' : `all ${done.length} jobs`}. Thank you${w.doc === 'estimate' ? ' — we will get started' : ''}.`
    : yes === 0 ? 'You declined this estimate.' : `You approved ${yes} of ${done.length} jobs. Thank you — we will do the ones you approved.`;
  return (
    <>
      <div className="no-print portal-est-bar">
        {!open
          ? <div className={'note-bar ' + (jobs.length ? (yes === 0 ? 'warn' : '') : w.est_status === 'declined' ? 'warn' : '')}><Icon name="check" size={16} />
              <span className="grow">{result}{!closed && <><br /><small>To change an answer, please call us{shop.shop_phone ? <> at <a href={`tel:${shop.shop_phone}`}>{shop.shop_phone}</a></> : ''}.</small></>}
                {!closed && ((data.recommendations || []) as Row[]).some(r => r.status === 'open') && <><br /><b>We also recommend more work — see “Recommended” below and tap Request on what you want done now.</b></>}</span></div>
          : jobs.length > 0
            ? <div className="note-bar"><Icon name="check" size={16} /><span className="grow"><b>{more ? 'We found more work on your truck.' : 'Please review this estimate.'}</b> Tap <b>Approve</b> or <b>Decline</b> on each job{more ? ' marked New' : ''}, sign with your name at the bottom, and send.</span></div>
            : <div className="est-decide">
                <b>Do you approve this estimate?</b>
                <textarea placeholder="Note to the shop (optional)" value={note} onChange={e => setNote(e.target.value)} rows={2} />
                <label className="field"><span>Your first and last name (signature)</span><input value={name} onChange={e => { setName(e.target.value); setNameBad(false); }} autoComplete="name" className={nameBad ? 'bad' : ''} /></label>
                <div className="actions-row"><Button kind="primary" icon="check" busy={busy} disabled={!nameOk} onClick={() => void send({ decision: 'approve' })}>Approve — go ahead</Button>
                  <Button kind="danger" busy={busy} disabled={!nameOk} onClick={() => void send({ decision: 'decline' })}>Decline</Button>
                  <Button onClick={() => window.print()} icon="printer">Print / PDF</Button></div>
                {err && <small className="danger">{err}</small>}
              </div>}
      </div>
      <EstimateSheet e={data} shop={data.shop} decide={open && jobs.length ? { pick, set: setOne, missing } : undefined}
        wantRec={closed ? undefined : async rid => { try { await api(`/api/portal/estimates/${id}/recs/${rid}`, { body: {} }); reload(); } catch (e) { setErr((e as Error).message); } }} />
      <SharedPhotos photos={(data.photos || []) as Row[]} src={pid => (DEMO ? photoSrc(pid) : `/api/portal/photos/${pid}`)} />
      {open && jobs.length > 0 && (
        <div className="est-dock no-print">
          <div className="dock-row">
            <span className="dock-sum">{more ? 'New work you approve' : 'You approve'}: <b>{dollars(okSum)}</b> <small>before tax &amp; supplies</small></span>
            <span className={'dock-left' + (left ? '' : ' ok')}>{left ? `${left} job${left > 1 ? 's' : ''} left to answer` : '✓ Every job answered'}</span>
          </div>
          <textarea placeholder="Note to the shop (optional) — e.g. call me before replacing the tires" value={note} onChange={e => setNote(e.target.value)} rows={1} />
          <div className="dock-row">
            <input className={'dock-name' + (nameBad ? ' bad' : '')} placeholder="Type your first and last name to sign" value={name} onChange={e => { setName(e.target.value); setNameBad(false); if (err.includes('name')) setErr(''); }} autoComplete="name" aria-label="Your first and last name (signature)" />
            <Button kind="primary" icon="check" busy={busy} onClick={trySend}>Sign &amp; send</Button>
          </div>
          <small className="muted">You can change your choices until you press Send — after that, call us to make a change. The date and time are added when you send.</small>
          {err && <small className="danger">{err}</small>}
        </div>
      )}
    </>
  );
}
